<template>
  <div class="completion-picker">
    <label>
      Отметка прохождения (необязательно)
      <select
        :value="modelValue"
        class="input"
        :disabled="disabled"
        @change="onSelectChange"
      >
        <option value="">Без отметки</option>
        <option
          v-for="completion in selectableCompletions"
          :key="completion.id"
          :value="String(completion.id)"
        >
          {{ formatCompletionLabel(completion) }}
        </option>
      </select>
    </label>

    <button
      type="button"
      class="btn btn-secondary"
      :disabled="disabled || creating"
      @click="showCreate = !showCreate"
    >
      {{ showCreate ? 'Скрыть форму' : 'Создать отметку' }}
    </button>

    <div v-if="showCreate" class="completion-picker-create panel">
      <label>
        Трек
        <select v-model="newTrackId" class="input" required>
          <option value="" disabled>Выберите трек</option>
          <option v-for="track in tracks" :key="track.id" :value="String(track.id)">
            {{ track.name }}
          </option>
        </select>
      </label>

      <label>
        Дата прохождения
        <input v-model="newCompletedAt" type="date" class="input" :max="today" required />
      </label>

      <VehiclePicker
        v-model="newVehicleId"
        :disabled="disabled"
        @quick-create="onQuickCreateVehicle"
      />

      <p v-if="createError" class="error-message">{{ createError }}</p>

      <button
        type="button"
        class="btn"
        :disabled="disabled || creating || !canCreate"
        @click="createCompletionMark"
      >
        {{ creating ? 'Сохранение...' : 'Сохранить отметку' }}
      </button>
    </div>

    <p v-if="loadError" class="error-message">{{ loadError }}</p>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { createCompletion } from '../api/completions'
import { fetchTrackCompletions } from '../api/trackCompletions'
import { fetchTracks } from '../api/tracks'
import VehiclePicker from './VehiclePicker.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  currentCompletionId: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'created', 'selected'])

const allCompletions = ref([])
const tracks = ref([])
const loadError = ref('')
const createError = ref('')
const creating = ref(false)
const showCreate = ref(false)
const newTrackId = ref('')
const newCompletedAt = ref(new Date().toISOString().slice(0, 10))
const newVehicleId = ref('')
const quickCreateVehicle = ref(null)

const today = new Date().toISOString().slice(0, 10)

const canCreate = computed(() => {
  if (!newTrackId.value || !newCompletedAt.value) return false
  if (quickCreateVehicle.value?.make?.trim()) return true
  return Boolean(newVehicleId.value)
})

const selectableCompletions = computed(() => {
  return allCompletions.value.filter(
    (completion) =>
      !completion.has_trip || String(completion.id) === props.currentCompletionId
  )
})

function onSelectChange(event) {
  const value = event.target.value
  emit('update:modelValue', value)
  if (!value) return

  const completion = allCompletions.value.find((item) => String(item.id) === value)
  if (completion) emit('selected', completion)
}

function formatCompletionLabel(completion) {
  const trackName = completion.track?.name || 'Трек'
  const date = formatDate(completion.completed_at)
  const vehicle = completion.vehicle?.make
  return [trackName, date, vehicle].filter(Boolean).join(' · ')
}

function formatDate(value) {
  if (!value) return ''
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium' }).format(new Date(value))
}

function onQuickCreateVehicle(payload) {
  quickCreateVehicle.value = payload
  if (payload) newVehicleId.value = ''
}

async function loadCompletions() {
  try {
    const { data } = await fetchTrackCompletions()
    allCompletions.value = data
  } catch {
    loadError.value = 'Не удалось загрузить отметки прохождения'
  }
}

async function loadTracks() {
  const { data } = await fetchTracks()
  tracks.value = data
}

async function createCompletionMark() {
  creating.value = true
  createError.value = ''
  try {
    const payload = quickCreateVehicle.value?.make?.trim()
      ? {
          completedAt: newCompletedAt.value,
          vehicle: {
            make: quickCreateVehicle.value.make,
            equipment: quickCreateVehicle.value.equipment,
          },
          photos: quickCreateVehicle.value.photos || [],
        }
      : {
          completedAt: newCompletedAt.value,
          vehicleId: Number(newVehicleId.value),
        }

    const { data } = await createCompletion(newTrackId.value, payload)
    allCompletions.value = [data, ...allCompletions.value]
    emit('update:modelValue', String(data.id))
    emit('created', data)
    emit('selected', data)
    showCreate.value = false
    newTrackId.value = ''
    newVehicleId.value = ''
    quickCreateVehicle.value = null
  } catch (err) {
    createError.value =
      err.response?.data?.errors?.join(', ') || 'Не удалось создать отметку прохождения'
  } finally {
    creating.value = false
  }
}

onMounted(async () => {
  await Promise.all([loadCompletions(), loadTracks()])
})

defineExpose({ reload: loadCompletions })
</script>

<style scoped>
.completion-picker {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.completion-picker label {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-weight: 600;
}

.completion-picker-create {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
}
</style>
